import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NbDialogRef, NbDialogService } from '@nebular/theme';
import { SsoApplicationsService } from 'src/app/services/sso-applications.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { SecretDisplayDialogComponent } from '../../developer-clients/secret-display-dialog/secret-display-dialog.component';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-sso-application-form',
  templateUrl: './sso-application-form.component.html',
  styleUrls: ['./sso-application-form.component.scss']
})
export class SsoApplicationFormComponent implements OnInit {
  @Input() clientId: string = '';

  appForm: any;
  isEditMode: boolean = false;
  redirectUris: string[] = [''];
  postLogoutRedirectUris: string[] = [];

  readonly availableRoles: string[] = ['admin', 'manager', 'chain_manager', 'receipt_auditor'];
  readonly availableScopes: string[] = ['openid', 'profile', 'email', 'role', 'stores', 'chains'];
  selectedRoles: string[] = [];
  selectedScopes: string[] = ['openid', 'profile', 'email'];

  constructor(
    private dialogRef: NbDialogRef<SsoApplicationFormComponent>,
    private ssoService: SsoApplicationsService,
    private dialogService: NbDialogService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.isEditMode = !!this.clientId;

    this.appForm = new FormGroup({
      name: new FormControl('', Validators.required),
      description: new FormControl('')
    });

    if (this.isEditMode) {
      this.loadApplication();
    }
  }

  loadApplication(): void {
    this.spinner.show();
    this.ssoService.getApplication(this.clientId).subscribe({
      next: (res: any) => {
        this.spinner.hide();
        const app = res.response?.data || res;
        this.appForm.patchValue({
          name: app.name,
          description: app.description || ''
        });
        this.redirectUris = app.redirectUris?.length ? [...app.redirectUris] : [''];
        this.postLogoutRedirectUris = app.postLogoutRedirectUris?.length ? [...app.postLogoutRedirectUris] : [];
        this.selectedRoles = [...(app.allowedRoles || [])];
        this.selectedScopes = this.ensureOpenidScope([...(app.scopes || ['openid', 'profile', 'email'])]);
      },
      error: (err: any) => {
        this.spinner.hide();
        Swal.fire('Error', err.error?.response?.message || 'Failed to load application', 'error');
        this.dialogRef.close();
      }
    });
  }

  addRedirectUri(): void {
    this.redirectUris.push('');
  }

  removeRedirectUri(index: number): void {
    if (this.redirectUris.length > 1) {
      this.redirectUris.splice(index, 1);
    }
  }

  addPostLogoutUri(): void {
    this.postLogoutRedirectUris.push('');
  }

  removePostLogoutUri(index: number): void {
    this.postLogoutRedirectUris.splice(index, 1);
  }

  toggleRole(role: string): void {
    const idx = this.selectedRoles.indexOf(role);
    if (idx > -1) {
      this.selectedRoles.splice(idx, 1);
    } else {
      this.selectedRoles.push(role);
    }
  }

  isRoleSelected(role: string): boolean {
    return this.selectedRoles.includes(role);
  }

  toggleScope(scope: string): void {
    if (scope === 'openid') {
      return;
    }
    const idx = this.selectedScopes.indexOf(scope);
    if (idx > -1) {
      this.selectedScopes.splice(idx, 1);
    } else {
      this.selectedScopes.push(scope);
    }
  }

  isScopeSelected(scope: string): boolean {
    return this.selectedScopes.includes(scope);
  }

  isScopeLocked(scope: string): boolean {
    return scope === 'openid';
  }

  private ensureOpenidScope(scopes: string[]): string[] {
    if (!scopes.includes('openid')) {
      scopes.unshift('openid');
    }
    return scopes;
  }

  private isValidUrl(url: string): boolean {
    const trimmed = url.trim();
    return /^https?:\/\/.+/.test(trimmed);
  }

  private getValidUris(uris: string[]): string[] {
    return uris.map(u => u.trim()).filter(u => u.length > 0);
  }

  private validateForm(): boolean {
    if (this.appForm.invalid) {
      this.toastr.error('Please fill all required fields');
      return false;
    }

    const validRedirectUris = this.getValidUris(this.redirectUris);
    if (validRedirectUris.length === 0) {
      this.toastr.error('At least one redirect URI is required');
      return false;
    }
    for (const uri of validRedirectUris) {
      if (!this.isValidUrl(uri)) {
        this.toastr.error(`Invalid redirect URI: ${uri}. Must start with http:// or https://`);
        return false;
      }
    }

    const validPostLogoutUris = this.getValidUris(this.postLogoutRedirectUris);
    for (const uri of validPostLogoutUris) {
      if (!this.isValidUrl(uri)) {
        this.toastr.error(`Invalid post-logout redirect URI: ${uri}. Must start with http:// or https://`);
        return false;
      }
    }

    if (this.selectedRoles.length === 0) {
      this.toastr.error('Please select at least one allowed role');
      return false;
    }

    return true;
  }

  onSubmit(): void {
    if (!this.validateForm()) {
      return;
    }

    const data: any = {
      name: this.appForm.get('name').value,
      description: this.appForm.get('description').value || undefined,
      redirectUris: this.getValidUris(this.redirectUris),
      postLogoutRedirectUris: this.getValidUris(this.postLogoutRedirectUris),
      allowedRoles: this.selectedRoles,
      scopes: this.ensureOpenidScope([...this.selectedScopes])
    };

    this.spinner.show();

    if (this.isEditMode) {
      this.ssoService.updateApplication(this.clientId, data).subscribe({
        next: () => {
          this.spinner.hide();
          this.toastr.success('Application updated');
          this.dialogRef.close({ saved: true });
        },
        error: (err: any) => {
          this.spinner.hide();
          Swal.fire('Error', err.error?.response?.message || 'Failed to update application', 'error');
        }
      });
    } else {
      this.ssoService.createApplication(data).subscribe({
        next: (res: any) => {
          this.spinner.hide();
          const result = res.response?.data || res;
          const clientId = result.clientId;
          const clientSecret = result.client_secret;
          this.dialogRef.close({ saved: true });

          this.dialogService.open(SecretDisplayDialogComponent, {
            context: { clientId, clientSecret, title: 'SSO Application Created' }
          });
        },
        error: (err: any) => {
          this.spinner.hide();
          Swal.fire('Error', err.error?.response?.message || 'Failed to create application', 'error');
        }
      });
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
